import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import ViewIconSmall from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import { useCompanyReportTranslation } from '@/pages/CompanyReport/hooks/useTranslation';
import type { ReactNode } from 'react';

type CardProps = {
  count?: number;
  slug?: string;
  children?: ReactNode;
};

const portfolioIcon = (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    className="-mt-0.5 inline-block size-3.5 align-middle"
  >
    <path d="M21 12c.552 0 1.005-.449.95-.998a10 10 0 0 0-8.953-8.951c-.55-.055-.998.398-.998.95v8a1 1 0 0 0 1 1z" />
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
  </svg>
);

export const Card = ({ count, slug, children }: CardProps) => {
  const { trackEvent } = useTracking();
  const t = useCompanyReportTranslation();

  const cta = {
    contents: (
      <>
        {t('header.portfolio.view')}
        <ViewIconSmall className="mt-px size-[13px] fill-current" />
        {/* Larger hit area */}
        <div className="absolute -inset-2 opacity-0" />
      </>
    ),
    className:
      'relative grid shrink-0 grid-cols-[1fr_auto] items-center gap-px whitespace-nowrap text-tiny text-accent no-underline hover:text-solid focus-visible:ring',
  };

  return (
    <div className="w-full @container/portfolio">
      <div className="flex flex-wrap gap-x-2 gap-y-px">
        <div className="grid grid-cols-[auto_1fr] items-center gap-1 whitespace-nowrap font-medium text-soft print:text-p-black">
          <span className="-mt-px text-softer print:text-p-black">
            {portfolioIcon}
          </span>
          {/* TODO: i18n */}
          {(count ?? 0) > 1
            ? t('header.portfolio.positionCount', {
                count,
              })
            : t('header.portfolio.value')}
        </div>
        <div className="flex grow items-center justify-between gap-inherit">
          <div className="flex w-full place-items-center gap-inherit whitespace-nowrap text-solid">
            {children}
          </div>
          {slug ? (
            <RouterLink
              to={slug}
              onClick={() => {
                trackEvent({
                  type: 'track',
                  action: 'click',
                  modifier: 'view',
                  subject: 'portfolioPosition',
                });
              }}
              className={cta.className}
            >
              {cta.contents}
            </RouterLink>
          ) : (
            <div className={cta.className}>{cta.contents}</div>
          )}
        </div>
      </div>
    </div>
  );
};
